import React, { useContext, useMemo } from "react";
import { LabelContext } from "../labels/LabelContext";
import { GroupContext } from '../layout/GroupContext';
import { ValidationState, InlineOrBlockSize } from "../Types";
import classNames from "classnames";
import styles from "./Select.scss";

export interface SelectOption<T> {
  label: string;
  value: T;
}

export interface SelectProps<T> {
  /**
   * Id of the element
   */
  id?: string;
  /**
   * Name of the element
   */
  name?: string;
  /**
   * Id of the HTML element describing the input
   */
  describedBy?: string;
  /**
   * Options specifying Radio Buttons
   */
  options: SelectOption<T>[];
  /**
   * Value of the selected Radio Button
   */
  value: T | null;
  /**
   * Value change handler
   */
  onChange: (v: T | null) => any;
  /**
   * Size
   */
  size?: InlineOrBlockSize;
  /**
   * Element validation state
   */
  state?: ValidationState;
  /**
   * Disables element
   */
  disabled?: boolean;
  /**
   * Use dark color scheme
   */
  defaultLabel?: string;
  /**
   * Custom class
   */
  className?: string;
}

/**
 * Displays lists of radio buttons
 */
export const Select = <T extends unknown>(props: SelectProps<T>) => {

  const cache = useMemo(() => {
    return props.options.reduce((map, o) => {
      map[`${o.value}`] = o.value;
      return map;
    }, {} as { [key: string]: T });
  }, [props.options]);

  const onChange = (v: string) => {
    if (v == "") {
      props.onChange(null);
    } else if (v in cache) {
      props.onChange(cache[v]);
    }
  };

  const labelCtx = useContext(LabelContext);
  const groupCtx = useContext(GroupContext);
  const state = labelCtx.state || props.state;

  return (
    <select
      id={props.id || labelCtx.id}
      name={props.name}
      value={(props.value === null || props.value === undefined) ? "" : `${props.value}`}
      onChange={(e) => onChange(e.target.value)}
      className={classNames(styles["au-select"], {
        [styles[`au-select--${state}`]]: state,
        [styles[`au-select--block`]]: props.size == 'block'
      }, groupCtx.className, props.className)}
      aria-labelledby={props.describedBy || labelCtx.describedBy}
      disabled={props.disabled}
    >
      {props.defaultLabel && <option value="">{props.defaultLabel}</option>}
      {props.options.map((o) => (
        <option value={`${o.value}`} key={`${o.value}`}>
          {o.label}
        </option>
      ))}
    </select>
  );
};
